import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@services/auth.service'; // Adjust path as needed
import { UtilsHelper } from 'src/app/shared/utils.helper'; // Adjust path as needed

export interface AuthInitializationResult {
  loggedIn: boolean;
  redirectTo?: string;
  tokenProcessed: boolean; // Flag to indicate if a token from URL was processed
  tokenValue?: string | null; // The value of the token processed
}

@Injectable({
  providedIn: 'root',
})
export class AuthInitializationService {
  constructor(
    private router: Router, // Router might not be strictly needed here based on current logic, but good for future flexibility
    private authService: AuthService,
    private activatedRoute:ActivatedRoute
  ) {}

  async initializeAppAuth(): Promise<AuthInitializationResult> {
    const searchParams = new URLSearchParams(window.location.search);
    const tokenFromQuery = searchParams.get('token');
    const authFromQuery = searchParams.get('auth');

    let tokenToValidate: string | null = null;
    let usedQueryParam: 'token' | 'auth' | null = null;

    if (tokenFromQuery) {
      tokenToValidate = tokenFromQuery;
      usedQueryParam = 'token';
    } else if (authFromQuery) {
      tokenToValidate = authFromQuery;
      usedQueryParam = 'auth';
    }

    if (tokenToValidate) {
      // Clear the token from the URL immediately after reading it
      // To prevent it from being visible or reprocessed on subsequent navigation or reloads.
      // The actual navigation decision will be made based on the validation result.
      // this.router.navigate([], {
      //   queryParams: { [usedQueryParam!]: null },
      //   queryParamsHandling: 'merge',
      //   replaceUrl: true
      // });
      
      try {
        console.log(`AuthInitializationService: Validating ${usedQueryParam}=${tokenToValidate}`);
        const currentPath = window.location.pathname;
        // Actual call to authService.validateAndSetSession
        // This method needs to exist in AuthService and return a boolean.
        const validationSuccess = await this.authService.validateAndSetSession(tokenToValidate);

        if (validationSuccess) {
          console.log('AuthInitializationService: Token validation successful.');
          // UtilsHelper.IsLoggedIn should be true now, set by authService.validateAndSetSession
          // Redirect to dashboard if currently on login or root, otherwise stay or go to intended deep link.
          const redirectTo = (currentPath === '/auth/login' || currentPath === '/' || currentPath === '') 
                             ? '/dashboard/chats' 
                             : currentPath; 

          return {
            loggedIn: true,
            redirectTo: redirectTo,
            tokenProcessed: true,
            tokenValue: tokenToValidate
          };
        } else {
          console.log('AuthInitializationService: Token validation failed by authService.');
          // UtilsHelper.clearAll() should have been called by authService.validateAndSetSession on failure.
          // UtilsHelper.IsLoggedIn should be false.
          return { 
            loggedIn: false, 
            redirectTo: '/auth/login', // Explicitly redirect to login on validation failure
            tokenProcessed: true, 
            tokenValue: tokenToValidate 
          };
        }
      } catch (error) {
        console.error('AuthInitializationService: Error during token validation process', error);
        UtilsHelper.clearAll(); // Ensure session is cleared on unexpected error
        // Manually ensure IsLoggedIn is false if an unexpected error occurs
        Object.defineProperty(UtilsHelper, 'IsLoggedIn', { value: false, writable: true }); 
        return { 
          loggedIn: false, 
          redirectTo: '/auth/login', // Explicitly redirect to login on error
          tokenProcessed: true, 
          tokenValue: tokenToValidate 
        };
      }
    } else {
      // No token in URL, respect existing session if any
      // If already logged in, and on root/login page, redirect to dashboard
      if (UtilsHelper.IsLoggedIn) {
        const currentPath = window.location.pathname;
        if (currentPath === '/auth/login' || currentPath === '/' || currentPath === '') {
          return { loggedIn: true, tokenProcessed: false, redirectTo: '/dashboard/chats' };
        }
      }
      return { loggedIn: UtilsHelper.IsLoggedIn, tokenProcessed: false };
    }
  }
}